// Per-step cadence for a recurring checklist.
//
// A step is an Obsidian Tasks line: `Sunlife Insurance 🔁 every 3 months 📅 2026-11-15`.
// The checklist as a whole runs on a monthly cycle (the engine clears its ticks when
// the month rolls over, handlers/quests.ts "step"), and this module decides which
// steps take part in a given month. Before it, every 🔁 step was treated as monthly:
// a quarterly premium came back unticked each month, counted in the n/13 total and
// kept the quest's XP locked in months where nothing was due.
//
// Only month-sized recurrences phase in and out: `every month`, `every N months`,
// `every year`, `every N years`, each anchored on the step's 📅 date. Anything finer
// (weekly, daily), or a cadence with no anchor to phase it, runs every month like a
// plain step. The note's dates never move; the occurrence in a given month is derived
// from the anchor (day of month, clamped), so a 📅 2026-08-26 line reads Sep 26 in
// September.
//
// Pure and import-free: web/hud/test/cadence.test.ts runs it under node --test.

export interface StepInfo { label: string; due?: string; recur?: string }

// A step's part in one month. Active: `on` is its date this cycle ("" when undated).
// Inactive: `on` is the next date it comes due.
export interface StepStatus { active: boolean; on: string }

const DUE_RE = /📅\s*(\d{4}-\d{2}-\d{2})/;
const RECUR_RE = /🔁\s*([A-Za-z0-9][A-Za-z0-9 ]*)/;
const MONTH_RE = /^every(?:\s+(\d+))?\s+(month|months|year|years)\b/i;

// Split the Obsidian Tasks fields off the label so they render as chips, not text.
export function parseStep(raw: string): StepInfo {
  const dm = DUE_RE.exec(raw);
  const rm = RECUR_RE.exec(raw);
  const label = raw.replace(DUE_RE, " ").replace(RECUR_RE, " ").replace(/\s+/g, " ").trim();
  return { label, due: dm?.[1], recur: rm?.[1]?.trim() };
}

// Months between occurrences, or null when the recurrence is not month-sized.
export function monthCadence(recur: string | undefined): number | null {
  if (!recur) return null;
  const m = MONTH_RE.exec(recur.trim());
  if (!m) return null;
  const n = m[1] ? Number(m[1]) : 1;
  if (!Number.isInteger(n) || n < 1) return null;
  return (m[2] ?? "").toLowerCase().startsWith("year") ? n * 12 : n;
}

const pad = (n: number): string => String(n).padStart(2, "0");
const ym = (month: string): { y: number; m: number } => ({ y: Number(month.slice(0, 4)), m: Number(month.slice(5, 7)) });

// "2026-11" to "2027-02" is 3.
export function monthsBetween(a: string, b: string): number {
  const x = ym(a);
  const y = ym(b);
  return (y.y - x.y) * 12 + (y.m - x.m);
}

function addMonths(month: string, n: number): string {
  const { y, m } = ym(month);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

// The anchor's day of month, in `month`, clamped: a bill on the 31st falls on Sep 30.
export function dateInMonth(month: string, day: number): string {
  const { y, m } = ym(month);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${pad(Math.max(1, Math.min(day, last)))}`;
}

export function stepStatus(step: StepInfo | string, month: string): StepStatus {
  const s = typeof step === "string" ? parseStep(step) : step;
  const every = monthCadence(s.recur);
  const anchor = s.due ?? "";
  if (every === null || !anchor) return { active: true, on: anchor };
  const anchorMonth = anchor.slice(0, 7);
  const day = Number(anchor.slice(8, 10));
  const diff = monthsBetween(anchorMonth, month);
  // Not started yet: the first occurrence is the anchor itself.
  if (diff < 0) return { active: false, on: anchor };
  if (diff % every === 0) return { active: true, on: dateInMonth(month, day) };
  return { active: false, on: dateInMonth(addMonths(anchorMonth, Math.ceil(diff / every) * every), day) };
}

// Indices of the steps that take part this month. Indices are the note's line order,
// which is what the persisted ticks (steps_done) are keyed by, so they are kept as is.
export function activeSteps(steps: string[], month: string): number[] {
  const out: number[] = [];
  steps.forEach((t, i) => { if (stepStatus(t, month).active) out.push(i); });
  return out;
}

// The first date after `month` on which a step is active: when the checklist's next
// cycle begins. The engine reopens a finished recurring checklist on the first read
// of that month and dates it here (handlers/quests.ts reopenStaleDailies). Steps
// without a month cadence run every month, so they land on the first of the next.
export function nextCycle(steps: string[], month: string): string {
  let best = "";
  for (const t of steps) {
    const on = nextOccurrence(parseStep(t), month);
    if (!best || on < best) best = on;
  }
  return best;
}

function nextOccurrence(s: StepInfo, month: string): string {
  const every = monthCadence(s.recur);
  const anchor = s.due ?? "";
  if (every === null || !anchor) return `${addMonths(month, 1)}-01`;
  const anchorMonth = anchor.slice(0, 7);
  const diff = monthsBetween(anchorMonth, month);
  if (diff < 0) return anchor;
  return dateInMonth(addMonths(anchorMonth, (Math.floor(diff / every) + 1) * every), Number(anchor.slice(8, 10)));
}
